import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Printer, Play, Unlock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];

export default function TemplateView() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [tpl, setTpl] = useState(null);
  const [settings, setSettings] = useState(null);
  const [depts, setDepts] = useState([]);
  const [users, setUsers] = useState([]);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    (async () => {
      const [t, s, d, u] = await Promise.all([
        api.get(`/templates/${id}`),
        api.get("/settings"),
        api.get("/departments"),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setTpl(t.data); setSettings(s.data); setDepts(d.data); setUsers(u.data);
    })();
  }, [id]);

  const grouped = useMemo(() => {
    if (!tpl) return [];
    const map = {};
    tpl.rows.forEach((r) => {
      const key = r.perspective || "(no perspective)";
      if (!map[key]) map[key] = [];
      map[key].push(r);
    });
    return Object.entries(map);
  }, [tpl]);

  if (!tpl) return <div className="text-sm text-zinc-500">Loading…</div>;
  const dept = depts.find((d) => d.id === tpl.department_id);
  const ratingScale = settings?.rating_scale || [];
  const adj = settings?.rating_adjustment;
  const canStart = tpl.status === "approved" && ["super_admin", "bu_owner", "bu_manager"].includes(user?.role);
  const canUnlock = tpl.status === "approved" && user?.role === "super_admin";

  const unlock = async () => {
    if (!confirm(
      "Unlock this template?\n\n" +
      "It will return to DRAFT status. The 3-step approval workflow (Manager submit → BU Owner approve → Super Admin sign-off) " +
      "must run again before reviews can use it. Existing reviews already created from this template are unaffected."
    )) return;
    try {
      await api.post(`/templates/${tpl.id}/unlock`);
      toast.success("Template unlocked — re-submit when edits are done");
      nav(`/templates/${tpl.id}/edit`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 print:hidden">
        <button onClick={() => nav(-1)} className="text-sm text-zinc-600 hover:text-zinc-900 flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex gap-2">
          <button onClick={() => window.print()} className="border border-zinc-300 px-4 py-2 rounded-sm text-sm hover:bg-zinc-50 flex items-center gap-1">
            <Printer className="w-4 h-4" /> Print
          </button>
          {canUnlock && (
            <button
              onClick={unlock}
              data-testid="template-unlock-btn"
              className="border border-amber-300 bg-amber-50 text-amber-800 px-4 py-2 rounded-sm text-sm font-medium hover:bg-amber-100 flex items-center gap-1"
            >
              <Unlock className="w-4 h-4" /> Unlock for editing
            </button>
          )}
          {canStart && (
            <button
              onClick={() => setStarting(true)}
              data-testid="template-start-review-btn"
              style={{ background: "var(--brand-primary)" }}
              className="text-white px-4 py-2 rounded-sm text-sm font-medium hover:opacity-90 flex items-center gap-2"
            >
              <Play className="w-4 h-4" /> Start review
            </button>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto bg-white border border-zinc-300 shadow-sm">
        <div className="px-10 py-8 border-b-2 border-zinc-900 flex items-start gap-6">
          <BuLogo buId={tpl.business_unit_id} />
          <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Performance Review · {tpl.fiscal_year}</div>
          <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{tpl.name}</h1>
          <div className="mt-3 grid grid-cols-4 gap-6 text-sm">
            <Meta label="Position Profile" value={dept?.name || "—"} />
            <Meta label="Fiscal Year" value={tpl.fiscal_year} />
            <Meta label="Status" value={tpl.status === "approved" ? "Locked" : tpl.status} />
            <Meta label="Rows" value={tpl.rows.length} />
          </div>
          </div>
        </div>

        {/* Legend */}
        <div className="px-10 py-5 border-b border-zinc-200 bg-zinc-50 grid grid-cols-2 gap-8">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-700 mb-2">Rating Scale</div>
            <table className="w-full text-xs border border-zinc-300">
              <thead className="bg-white"><tr><th className="text-left px-2 py-1 border border-zinc-300 w-12">Score</th><th className="text-left px-2 py-1 border border-zinc-300">Rating</th></tr></thead>
              <tbody>
                {ratingScale.map((r) => (
                  <tr key={r.score}>
                    <td className="px-2 py-1 border border-zinc-300 bg-white font-semibold tabular-nums">{r.score}</td>
                    <td className="px-2 py-1 border border-zinc-300 bg-white"><b>{r.label}</b><span className="text-zinc-500"> — {r.description}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-700 mb-2">Increase % Legend</div>
            <table className="w-full text-xs border border-zinc-300">
              <tbody>
                <tr><td className="px-2 py-1 border border-zinc-300 bg-white">Score ≥ {adj?.increase_threshold_score}</td><td className="px-2 py-1 border border-zinc-300 bg-white text-emerald-700 font-semibold">+{adj?.increase_percent}%</td></tr>
                <tr><td className="px-2 py-1 border border-zinc-300 bg-white">Score ≤ {adj?.decrease_threshold_score}</td><td className="px-2 py-1 border border-zinc-300 bg-white text-red-700 font-semibold">−{adj?.decrease_percent}%</td></tr>
                <tr><td className="px-2 py-1 border border-zinc-300 bg-white">Remcon approval required at</td><td className="px-2 py-1 border border-zinc-300 bg-white">Score ≥ {adj?.requires_remcon_approval_at}</td></tr>
              </tbody>
            </table>
            <div className="text-[10px] text-zinc-500 mt-2">KPA Score = KPA Rating × (Perspective KPA Weight ÷ 100)</div>
          </div>
        </div>

        <div className="px-10 py-8">
          <table className="w-full border-collapse border-2 border-zinc-900 text-sm">
            <thead>
              <tr className="bg-zinc-900 text-white text-[11px] uppercase tracking-wider">
                <th className="border border-zinc-900 px-3 py-2 text-left w-44">Perspective</th>
                <th className="border border-zinc-900 px-3 py-2 text-left">KPA Strategic Goal</th>
                <th className="border border-zinc-900 px-3 py-2 text-left">KPA</th>
                <th className="border border-zinc-900 px-3 py-2 text-center w-28">Perspective KPA Weight</th>
                <th className="border border-zinc-900 px-3 py-2 text-left w-40">Measurement Tool</th>
                <th className="border border-zinc-900 px-3 py-2 text-left">Comments</th>
                <th className="border border-zinc-900 px-3 py-2 text-center w-20">KPA Rating</th>
                <th className="border border-zinc-900 px-3 py-2 text-center w-20">KPA Score</th>
              </tr>
            </thead>
            <tbody>
              {grouped.map(([persp, rows]) =>
                rows.map((r, idx) => (
                  <tr key={r.id || `${persp}-${idx}`} className="align-top">
                    {idx === 0 && (
                      <td rowSpan={rows.length} className="border border-zinc-400 px-3 py-3 bg-zinc-50 font-semibold text-zinc-900">{persp}</td>
                    )}
                    <td className="border border-zinc-400 px-3 py-3">{r.strategic_goal || "—"}</td>
                    <td className="border border-zinc-400 px-3 py-3">{r.kpa}</td>
                    <td className="border border-zinc-400 px-3 py-3 text-center tabular-nums font-semibold">{r.weight}%</td>
                    <td className="border border-zinc-400 px-3 py-3">
                      {r.measurement_tool && <div>{r.measurement_tool}</div>}
                      {(r.target || r.frequency || r.source_of_truth) && (
                        <div className={`text-[11px] text-zinc-600 ${r.measurement_tool ? "mt-1 border-t border-zinc-200 pt-1" : ""}`}>
                          {r.target && <div><span className="text-zinc-500">Target: </span>{r.target}</div>}
                          {r.frequency && <div><span className="text-zinc-500">Frequency: </span>{r.frequency}</div>}
                          {r.source_of_truth && <div><span className="text-zinc-500">Source: </span>{r.source_of_truth}</div>}
                        </div>
                      )}
                      {!r.measurement_tool && !r.target && !r.frequency && !r.source_of_truth && "—"}
                    </td>
                    <td className="border border-zinc-400 px-3 py-3 text-zinc-600 text-xs whitespace-pre-wrap">{r.comments}</td>
                    <td className="border border-zinc-400 px-3 py-3 text-center text-zinc-400">—</td>
                    <td className="border border-zinc-400 px-3 py-3 text-center text-zinc-400">—</td>
                  </tr>
                ))
              )}
              {tpl.rows.length === 0 && (
                <tr><td colSpan={8} className="border border-zinc-400 text-center px-3 py-8 text-zinc-500">No KPA rows yet.</td></tr>
              )}
            </tbody>
            <tfoot>
              <tr className="bg-zinc-100 font-semibold">
                <td colSpan={3} className="border border-zinc-400 px-3 py-2 text-right">Total weight</td>
                <td className="border border-zinc-400 px-3 py-2 text-center tabular-nums">{tpl.rows.reduce((a, r) => a + Number(r.weight || 0), 0)}%</td>
                <td colSpan={4} className="border border-zinc-400 px-3 py-2"></td>
              </tr>
            </tfoot>
          </table>

          <div className="grid grid-cols-3 gap-6 mt-10">
            {["Manager Signature", "Employee Signature", "Final Meeting Signature"].map((l) => (
              <div key={l}>
                <div className="border-b-2 border-zinc-900 h-16"></div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">{l} · Date</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {starting && (
        <StartReviewModal tpl={tpl} users={users} onClose={() => setStarting(false)} />
      )}
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-sm font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function BuLogo({ buId }) {
  const [bu, setBu] = useState(null);
  useEffect(() => {
    if (!buId) return;
    api.get(`/business-units/${buId}`).then(({ data }) => setBu(data)).catch(() => {});
  }, [buId]);
  if (!bu?.logo) return null;
  return <img src={bu.logo} alt={bu.name} className="max-h-20 max-w-[180px] object-contain" />;
}

function StartReviewModal({ tpl, users, onClose }) {
  const [employeeId, setEmployeeId] = useState("");
  const [managerId, setManagerId] = useState("");
  const [quarter, setQuarter] = useState("Q1");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const deptEmployees = users.filter((u) => u.department_id === tpl.department_id && u.role !== "super_admin");
  const managers = users.filter((u) => u.role === "bu_manager" || u.role === "bu_owner" || u.role === "super_admin");

  const submit = async () => {
    if (!employeeId || !managerId) return toast.error("Choose employee and manager");
    setBusy(true);
    try {
      const { data } = await api.post("/reviews", { template_id: tpl.id, employee_id: employeeId, manager_id: managerId, quarter });
      toast.success(`${quarter} review created`);
      nav(`/reviews/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white border border-zinc-200 rounded-sm max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-zinc-200 text-sm font-semibold">Start KPA review</div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Quarter</label>
            <div className="mt-1 flex gap-2">
              {QUARTERS.map((q) => (
                <button key={q} onClick={() => setQuarter(q)} data-testid={`start-review-quarter-${q}`}
                  className={`flex-1 py-2 text-sm border rounded-sm ${quarter === q ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-300 hover:bg-zinc-50"}`}>{q}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Employee</label>
            <select data-testid="start-review-employee-select" className="mt-1 w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
              <option value="">— Choose —</option>
              {deptEmployees.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.title || u.role})</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">Manager</label>
            <select data-testid="start-review-manager-select" className="mt-1 w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white" value={managerId} onChange={(e) => setManagerId(e.target.value)}>
              <option value="">— Choose —</option>
              {managers.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
            </select>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-zinc-300 rounded-sm hover:bg-zinc-50">Cancel</button>
            <button data-testid="start-review-confirm-btn" disabled={busy} onClick={submit} className="px-4 py-2 text-sm bg-zinc-900 text-white rounded-sm hover:bg-zinc-800 disabled:opacity-60">{busy ? "Creating…" : "Create review"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
